import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Search, Loader2, RefreshCw, ExternalLink, X } from 'lucide-react';
import { apiFetch, getFileUrl } from '@/utils/api';
import PaginationFooter from '@/components/PaginationFooter';
import DocumentViewer from '@/components/DocumentViewer';
import { formatDate, formatAmountSummary } from '@/utils/formatters';
import { useI18n } from '@/hooks/useI18n';


interface Vendor {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
}

interface Invoice {
    id: string;
    vendor?: Vendor;
    cost_center?: CostCenter;
    total_amount?: number;
    created_at: string;
    invoice_date?: string;
    status: string;
    invoice_number?: string;
    has_invoice_photo?: boolean;
}

interface AIResultData {
    vendor?: {
        name?: string;
        tax_number?: string;
        address?: string;
    };
    invoice_number?: string;
    invoice_date?: string;
    total_amount?: number;
}

interface GoogleDriveFile {
    id: string;
    file_name: string;
    google_file_id: string;
    local_file_path?: string;
    mime_type?: string;
    extracted_invoice_number?: string;
    processing_status: string;
    error_message?: string;
    matched_invoice_id?: string;
    match_confidence?: string;
    match_method?: string;
    extraction_confidence?: string;
    processed_at?: string;
    created_at?: string;
    updated_at?: string;
    document_type?: string;
    ai_result_json?: string | AIResultData;  // Can be string from API or parsed object
    matched_invoice?: {
        id: string;
        invoice_number?: string;
        cost_center?: {
            id: string;
            name: string;
        };
    };
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

export default function ManualMatchPage() {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const searchParams = routerState.location.search as any;
    const { fileId, returnTo } = searchParams || {};
    const { t } = useI18n();

    const [gdriveFile, setGdriveFile] = useState<GoogleDriveFile | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [fileLoading, setFileLoading] = useState<boolean>(true);
    const [matching, setMatching] = useState<string | null>(null);
    const [reprocessingFile, setReprocessingFile] = useState<boolean>(false);
    const [creatingInvoice, setCreatingInvoice] = useState<boolean>(false);
    const [unmatchingFile, setUnmatchingFile] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 10,
    });

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset pagination when search changes
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [debouncedSearch]);

    // Fetch invoices when pagination or search changes
    useEffect(() => {
        if (gdriveFile) {
            fetchInvoices();
        }
    }, [pagination.page, pagination.limit, debouncedSearch, gdriveFile]);

    // Fetch Google Drive file details
    useEffect(() => {
        if (fileId) {
            fetchGdriveFile();
        }
    }, [fileId]);

    const fetchGdriveFile = async () => {
        if (!fileId) return;

        setFileLoading(true);
        try {
            const response = await apiFetch(`/api/gdrive-invoices/files/${fileId}`);
            if (response.ok) {
                const file = await response.json();

                // Parse ai_result_json if it's a string
                if (file.ai_result_json && typeof file.ai_result_json === 'string') {
                    try {
                        file.ai_result_json = JSON.parse(file.ai_result_json);
                    } catch (e) {
                        console.error('Failed to parse ai_result_json:', e);
                        file.ai_result_json = null;
                    }
                }

                setGdriveFile(file);
            } else {
                console.error('Failed to load Google Drive file:', response.status);
                // Redirect back if file not found
                const backUrl = returnTo ? decodeURIComponent(returnTo as string) : '/invoices/gdrive-files';
                navigate({ to: backUrl });
            }
        } catch (error) {
            console.error('Error loading Google Drive file:', error);
            const backUrl = returnTo ? decodeURIComponent(returnTo as string) : '/invoices/gdrive-files';
            navigate({ to: backUrl });
        } finally {
            setFileLoading(false);
        }
    };

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                // Filter to only show invoices without Google Drive files
                has_photo: 'false',
            });

            if (debouncedSearch) {
                params.set('search', debouncedSearch);
            }

            const response = await apiFetch(`/api/invoices/?${params}`);

            if (response.ok) {
                const data = await response.json();
                setInvoices(data.invoices || []);
                if (data.pagination) {
                    setPagination(prev => ({ ...prev, ...data.pagination }));
                }
            } else {
                console.error('Failed to load invoices:', response.status);
            }
        } catch (error) {
            console.error('Error loading invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMatch = async (invoiceId: string) => {
        if (!gdriveFile) return;

        setMatching(invoiceId);
        try {
            const response = await apiFetch('/api/gdrive-invoices/manual-match', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    gdrive_file_id: gdriveFile.id,
                    invoice_id: invoiceId,
                }),
            });

            if (response.ok) {
                // Navigate back to Google Drive files page
                const backUrl = returnTo ? decodeURIComponent(returnTo as string) : '/invoices/gdrive-files';
                navigate({ to: backUrl });
            } else {
                const errorData = await response.json();
                console.error('Failed to match:', errorData.detail || 'Unknown error');
                // Error handling without alerts - could add toast notification here if needed
            }
        } catch (error) {
            console.error('Error matching files:', error);
            // Error handling without alerts - could add toast notification here if needed
        } finally {
            setMatching(null);
        }
    };

    const handleReprocessFile = async () => {
        if (!gdriveFile) return;

        setReprocessingFile(true);
        try {
            const response = await apiFetch(`/api/gdrive-invoices/reprocess-file/${gdriveFile.id}`, {
                method: 'POST',
            });

            if (response.ok) {
                // Start polling for completion
                await pollForReprocessingCompletion();
            } else {
                console.error('Failed to start reprocessing');
                setReprocessingFile(false);
            }
        } catch (error) {
            console.error('Error reprocessing file:', error);
            setReprocessingFile(false);
        }
    };

    const handleProcessBankStatement = async () => {
        if (!gdriveFile) return;

        setReprocessingFile(true);
        try {
            const response = await apiFetch(`/api/gdrive-invoices/reprocess-file-as-bank-statement/${gdriveFile.id}`, {
                method: 'POST',
            });

            if (response.ok) {
                await pollForReprocessingCompletion();
            } else {
                console.error('Failed to start bank statement processing');
                setReprocessingFile(false);
            }
        } catch (error) {
            console.error('Error starting bank statement processing:', error);
            setReprocessingFile(false);
        }
    };


    const pollForReprocessingCompletion = async () => {
        const maxAttempts = 30; // Poll for up to 30 seconds
        let attempts = 0;

        const poll = async () => {
            attempts++;

            try {
                // Refresh file details to check status
                const response = await apiFetch(`/api/gdrive-invoices/files/${gdriveFile?.id}`);

                if (response.ok) {
                    const updatedFile = await response.json();

                    // Parse ai_result_json if it's a string
                    if (updatedFile.ai_result_json && typeof updatedFile.ai_result_json === 'string') {
                        try {
                            updatedFile.ai_result_json = JSON.parse(updatedFile.ai_result_json);
                        } catch (e) {
                            console.error('Failed to parse ai_result_json:', e);
                            updatedFile.ai_result_json = null;
                        }
                    }

                    // Check if processing is complete (not 'pending' or 'processing')
                    if (updatedFile.processing_status !== 'pending' && updatedFile.processing_status !== 'processing') {
                        // Processing complete - update the file and refresh everything
                        setGdriveFile(updatedFile);
                        setReprocessingFile(false);

                        // Refresh the invoice list as well since matching may have changed
                        fetchInvoices();
                        return;
                    }
                }

                // Continue polling if not complete and haven't exceeded max attempts
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000); // Poll every second
                } else {
                    // Timeout - stop polling but refresh once more
                    setReprocessingFile(false);
                    fetchGdriveFile();
                }
            } catch (error) {
                console.error('Error polling for reprocessing completion:', error);
                setReprocessingFile(false);
                fetchGdriveFile();
            }
        };

        // Start polling after a short delay
        setTimeout(poll, 2000);
    };

    const handleCreateInvoice = async () => {
        if (!gdriveFile) return;

        setCreatingInvoice(true);
        try {
            const response = await apiFetch(`/api/gdrive-invoices/create-invoice-from-file/${gdriveFile.id}`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();

                if (result.status === 'reprocessing') {
                    // Refresh the file details to show updated status
                    fetchGdriveFile();
                } else if (result.status === 'created') {
                    // Navigate to the new invoice; open in edit mode if requested by backend
                    // Preserve returnTo so user can navigate back to gdrive-files with filters intact
                    const search: any = {};
                    if (result.open_in_edit) search.edit = '1';
                    if (returnTo) search.returnTo = returnTo;
                    navigate({ to: `/invoices/${result.invoice_id}`, search });
                }
            } else {
                const errorData = await response.json();
                console.error('Failed to create invoice:', errorData.detail || 'Unknown error');
                // Error handling without alerts - could add toast notification here if needed
            }
        } catch (error) {
            console.error('Error creating invoice:', error);
            // Error handling without alerts - could add toast notification here if needed
        } finally {
            setCreatingInvoice(false);
        }
    };

    const handleUnmatchFile = async () => {
        if (!gdriveFile || !gdriveFile.matched_invoice_id) return;

        setUnmatchingFile(true);
        try {
            const response = await apiFetch(`/api/gdrive-invoices/unmatch-invoice/${gdriveFile.matched_invoice_id}`, {
                method: 'POST',
            });

            if (response.ok) {
                // Refresh the file to show updated status
                await fetchGdriveFile();
                // Also refresh the invoice list
                await fetchInvoices();
            } else {
                const data = await response.json().catch(() => ({}));
                console.error('Failed to unmatch file:', data.detail || 'Unknown error');
            }
        } catch (error) {
            console.error('Error unmatching file:', error);
        } finally {
            setUnmatchingFile(false);
        }
    };

    const handleGoBack = () => {
        const backUrl = returnTo ? decodeURIComponent(returnTo as string) : '/invoices/gdrive-files';
        navigate({ to: backUrl });
    };

    if (fileLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin mr-2" />
                    <span>Loading file details...</span>
                </div>
            </Layout>
        );
    }

    if (!gdriveFile) {
        return (
            <Layout>
                <div className="text-center py-8">
                    <p className="text-muted-foreground">File not found</p>
                    <Button onClick={handleGoBack} className="mt-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Google Drive Files
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={handleGoBack}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold">{t('gdriveFiles.manualMatch', 'finance')}</h1>
                            <p className="text-muted-foreground">
                                {t('gdriveFiles.manualMatchDesc', 'finance', { filename: gdriveFile.file_name })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left side - Invoice list */}
                    <div className={`space-y-4 ${reprocessingFile || creatingInvoice || unmatchingFile ? 'opacity-60 pointer-events-none' : ''}`}>
                        <div className="flex items-center justify-between">
                            <CardTitle>{t('gdriveFiles.selectInvoice', 'finance')}</CardTitle>
                            <div className="flex items-center gap-2">
                                <Search className="h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder={reprocessingFile || creatingInvoice || unmatchingFile ? "Processing..." : "Search invoices..."}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="flex-1"
                                    disabled={reprocessingFile || creatingInvoice || unmatchingFile}
                                />
                            </div>
                        </div>
                        <div className="overflow-hidden">
                            <div className="min-h-[600px]">
                                {loading ? (
                                    <div className="flex items-center justify-center p-8">
                                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                                        <span>Loading invoices...</span>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('gdriveFiles.invoiceNumber', 'finance')}</TableHead>
                                                <TableHead>{t('common.vendor', 'common')}</TableHead>
                                                <TableHead>{t('forms.date', 'common')}</TableHead>
                                                <TableHead>{t('forms.amount', 'common')}</TableHead>
                                                <TableHead>{t('invoices.status', 'finance')}</TableHead>
                                                <TableHead className="text-right"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {invoices.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                        {debouncedSearch ? 'No matching invoices found' : 'No invoices without photos found'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                invoices.map((invoice) => (
                                                    <TableRow key={invoice.id}>
                                                        <TableCell className="font-medium">
                                                            {invoice.invoice_number || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {invoice.vendor?.name || 'Unknown'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(invoice.invoice_date || invoice.created_at)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatAmountSummary(invoice.total_amount || 0)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={
                                                                invoice.status === 'verified' ? 'default' :
                                                                invoice.status === 'needs_review' ? 'secondary' :
                                                                'outline'
                                                            }>
                                                                {invoice.status}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleMatch(invoice.id)}
                                                                disabled={matching === invoice.id || reprocessingFile || creatingInvoice || unmatchingFile}
                                                            >
                                                                {matching === invoice.id ? (
                                                                    <>
                                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                                        Matching...
                                                                    </>
                                                                ) : (
                                                                    t('gdriveFiles.match', 'finance')
                                                                )}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>

                            {/* Pagination */}
                            <div className="p-4 border-t">
                                <PaginationFooter
                                    pagination={pagination}
                                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right side - Document preview */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>{t('gdriveFiles.documentPreview', 'finance')}</CardTitle>
                                <div className="flex items-center justify-right">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleReprocessFile}
                                    disabled={reprocessingFile || creatingInvoice || unmatchingFile}
                                >
                                    {reprocessingFile ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                            {t('gdriveFiles.reprocessing', 'finance')}
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="h-4 w-4 mr-1" />
                                            {t('gdriveFiles.reprocessFile', 'finance')}
                                        </>
                                    )}
                                </Button>
                                {/* Primary action based on document type */}
                                {gdriveFile.processing_status === 'processed' && !gdriveFile.matched_invoice_id && (
                                    <>
                                        {gdriveFile.document_type === 'bank_statement' && !gdriveFile.processed_at ? (
                                            // Bank statement that needs processing
                                            <Button
                                                size="sm"
                                                onClick={handleProcessBankStatement}
                                                disabled={reprocessingFile || creatingInvoice || unmatchingFile}
                                                className="ml-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                                {reprocessingFile ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                        {t('gdriveFiles.processingBank', 'finance')}
                                                    </>
                                                ) : (
                                                    t('gdriveFiles.processBankStatement', 'finance')
                                                )}
                                            </Button>
                                        ) : (
                                            // Only show "Create New Invoice" for actual invoices (not bank statements, delivery notes, or payment reports)
                                            gdriveFile.document_type !== 'delivery_note' &&
                                            gdriveFile.document_type !== 'payment_report' &&
                                            gdriveFile.document_type !== 'bank_statement' && (
                                                <Button
                                                    size="sm"
                                                    onClick={handleCreateInvoice}
                                                    disabled={creatingInvoice || reprocessingFile || unmatchingFile}
                                                    className="ml-3 bg-blue-600 hover:bg-blue-700 text-white"
                                                >
                                                    {creatingInvoice ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                            Creating...
                                                        </>
                                                    ) : (
                                                        t('gdriveFiles.createNewInvoice', 'finance')
                                                    )}
                                                </Button>
                                            )
                                        )}
                                    </>
                                )}
                                    </div>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm text-muted-foreground">{gdriveFile.file_name}</p>
                                {typeof gdriveFile.ai_result_json === 'object' && gdriveFile.ai_result_json?.vendor?.name && (
                                    <p className="text-sm text-muted-foreground">
                                        Vendor: <span className="font-medium">{gdriveFile.ai_result_json.vendor.name}</span>
                                    </p>
                                )}
                                {gdriveFile.extracted_invoice_number && (
                                    <p className="text-sm text-muted-foreground">
                                        {t('gdriveFiles.invoiceNumber', 'finance')}: {gdriveFile.extracted_invoice_number}
                                    </p>
                                )}
                                <p className="text-sm text-muted-foreground">
                                    {t('general.status', 'finance')}: <span className="capitalize">
                                        {reprocessingFile ? t('gdriveFiles.reprocessing', 'finance') : gdriveFile.processing_status}
                                    </span>
                                    {reprocessingFile && (
                                        <Loader2 className="h-3 w-3 animate-spin ml-1 inline" />
                                    )}
                                </p>
                            </div>
                        </CardHeader>
                        <CardContent>



                            {/* Processing result/error message */}
                            {gdriveFile.error_message && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <div className="flex items-start justify-between">
                                        <div className="text-sm text-red-800">
                                            <strong>{t('gdriveFiles.processingStatus', 'finance')}:</strong>
                                            <div className="mt-1">{gdriveFile.error_message}</div>
                                        </div>

                                    </div>
                                </div>
                            )}


                            {/* Processing success message for matched files */}
                            {gdriveFile.processing_status === 'matched' && gdriveFile.matched_invoice_id && !gdriveFile.error_message && (
                                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-green-800">
                                            <span className="block mb-2">
                                            <strong>Processing Complete:</strong> {gdriveFile.match_confidence && (
                                                <span className=" text-xs">
                                                    (Confidence: {Math.round(parseFloat(gdriveFile.match_confidence) * 100)}%)
                                                </span>
                                                )}
                                                </span>
                                            <span className='mt-1'>Successfully matched to invoice
                                            {gdriveFile.matched_invoice?.invoice_number && (
                                                <span className="ml-1 font-medium">
                                                    {gdriveFile.matched_invoice.invoice_number}
                                                </span>
                                            )}
                                            </span>
                                            {gdriveFile.matched_invoice?.cost_center && (
                                                <div className="mt-1">
                                                    Cost Center: {gdriveFile.matched_invoice.cost_center.name}
                                                </div>
                                            )}

                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => navigate({ to: `/invoices/${gdriveFile.matched_invoice_id}`})}
                                                className="text-green-700 border-green-300 hover:bg-green-100"
                                            >
                                                <ExternalLink className="h-4 w-4 mr-1" />
                                                Open Invoice
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={handleUnmatchFile}
                                                disabled={unmatchingFile}
                                            >
                                                {unmatchingFile ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                        {t('gdriveFiles.unmatching', 'finance')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <X className="h-4 w-4 mr-1" />
                                                        {t('gdriveFiles.unmatch', 'finance')}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="min-h-[600px] overflow-auto">
                                <DocumentViewer
                                    src={gdriveFile.local_file_path ? getFileUrl(gdriveFile.local_file_path) : null}
                                    mimeType={gdriveFile.mime_type}
                                    alt={gdriveFile.file_name}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/invoices/manual-match')({
  component: ManualMatchPage,
})
